import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, Shield, ShieldOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, authLoading]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/role?role=${newRole}`);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-serif text-3xl font-semibold text-stone-900">Users</h1>
            <p className="text-muted-foreground">Manage customer accounts</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">User</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Phone</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Role</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredUsers.map((userData) => (
                  <tr key={userData.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-medium">
                            {userData.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <p className="font-medium text-stone-900">{userData.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">{userData.email}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{userData.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        userData.role === 'admin' ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {userData.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {userData.id !== user.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRoleChange(userData.id, userData.role === 'admin' ? 'customer' : 'admin')}
                          className="gap-2"
                        >
                          {userData.role === 'admin' ? (
                            <>
                              <ShieldOff className="h-4 w-4" /> Remove Admin
                            </>
                          ) : (
                            <>
                              <Shield className="h-4 w-4" /> Make Admin
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No users found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
